import type { StageSpec } from "../config/campaign";
import { layoutStage, type StageFigure } from "./stage";

export type StageDrawable = StageFigure & { image: CanvasImageSource };

/** Altura máxima da dissolução no rodapé de uma figura, em fração da própria altura. */
const FIGURE_FADE_RATIO = 0.25;

function fadeOut(ctx: CanvasRenderingContext2D, top: number, bottom: number, width: number) {
  if (bottom <= top) return;
  const gradient = ctx.createLinearGradient(0, top, 0, bottom);
  gradient.addColorStop(0, "rgba(0,0,0,0)");
  gradient.addColorStop(1, "rgba(0,0,0,1)");
  ctx.save();
  ctx.globalCompositeOperation = "destination-out";
  ctx.fillStyle = gradient;
  ctx.fillRect(0, top, width, bottom - top);
  ctx.restore();
}

/**
 * Desenha as figuras alinhadas pela linha dos olhos numa camada própria e a compõe no destino.
 * Cada figura dissolve no próprio rodapé — os recortes têm alturas diferentes e um corte único
 * deixaria uma borda reta em quem termina antes. O corte na cutLine fecha o conjunto.
 */
export function drawStage(
  target: CanvasRenderingContext2D,
  figures: StageDrawable[],
  spec: StageSpec,
  width: number,
  height: number,
  usableWidth = width,
) {
  if (figures.length === 0) return;
  const placements = layoutStage(figures, spec, width, undefined, usableWidth);

  const layer = document.createElement("canvas");
  layer.width = width;
  layer.height = height;
  const ctx = layer.getContext("2d");
  if (!ctx) return;

  for (const placement of placements) {
    const figure = figures.find((item) => item.id === placement.id)!;
    const cell = document.createElement("canvas");
    cell.width = width;
    cell.height = height;
    const cellCtx = cell.getContext("2d");
    if (!cellCtx) continue;

    cellCtx.drawImage(figure.image, placement.x, placement.y, placement.width, placement.height);

    const bottom = placement.y + placement.height;
    if (bottom < spec.cutLine) {
      const fade = Math.min(spec.fade, placement.height * FIGURE_FADE_RATIO);
      fadeOut(cellCtx, bottom - fade, bottom, width);
    }

    ctx.drawImage(cell, 0, 0);
  }

  fadeOut(ctx, spec.cutLine - spec.fade, spec.cutLine, width);
  ctx.clearRect(0, spec.cutLine, width, height - spec.cutLine);

  target.drawImage(layer, 0, 0);
}
